import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, share, Subject, firstValueFrom } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { TokenService } from './token.service';
import { Store } from '@ngrx/store';
import * as AuthActions from '../store/auth/auth.actions';
import { AuthState } from '../models/auth.model';
import { AuthService } from '../services/auth.service';
import { GameSession } from '../models/game.model';
import { OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AIJobSocketResponse } from '../models/auth.model';
// Adjust to your payload
export interface SessionUpdate {
  session: GameSession | null;
  message: string;
}
import { filter } from 'rxjs/operators';

type ConnStatus = 'connected' | 'disconnected';

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {
  private rooms = new Set<string>();
  private socket: Socket | null = null;
  private readonly DEBUG = environment.debugLog;
  private streams = new Map<string, Observable<any>>();
  private latency$ = new BehaviorSubject<number>(0);
  private connectionStatus$ = new BehaviorSubject<ConnStatus>('disconnected');

  // ★ new: guard refresh loop + track reconnection toggle restore
  private refreshing = false;

  // Keep track of custom listeners so we can re-attach after reconnect
  private customListeners: Record<string, Array<(data: any) => void>> = {};

  private pingInterval: any;

  private currentSessionRoom: string | null = null;
  private readonly instanceId = Math.random().toString(36).slice(2);

  constructor(
    private tokenService: TokenService,
    private store: Store<{ auth: AuthState }>,
    private authService: AuthService,
    private snackBar: MatSnackBar,
  ) {
    this.debugLog('Initializing SocketService');
  }

  public setSessionRoom(sessionId: number | null) {
    this.debugLog('!!! setSessionRoom', sessionId);
    const next = sessionId ? `session_${sessionId}` : null;

    // leave previous
    if (this.currentSessionRoom && this.currentSessionRoom !== next) {
      this.leave(this.currentSessionRoom);
    }

    this.currentSessionRoom = next;

    if (next) this.join(next);
  }

  /** Register callback and return an unsubscribe fn (no leaks). Also stored for re-attach. */
  public registerCustomListener<T>(event: string, callback: (data: T) => void): () => void {
    this.debugLog('Registering custom listener:', event);

    if (!this.customListeners[event]) this.customListeners[event] = [];
    this.customListeners[event].push(callback);

    if (this.socket) this.socket.on(event, callback);

    // unsubscribe removes from both socket and cache
    return () => {
      this.debugLog('Unregistering custom listener:', event);
      const arr = this.customListeners[event];
      if (arr) {
        this.customListeners[event] = arr.filter(cb => cb !== callback);
        if (this.socket) this.socket.off(event, callback);
        if (this.customListeners[event].length === 0) delete this.customListeners[event];
      }
    };
  }

  // Replace your current on$ with this implementation:
  // public on$<T = unknown>(event: string): Observable<T> {
  //   return new Observable<T>(subscriber => {
  //     // Use registerCustomListener so the callback is stored and re-attached on every connect()
  //     const off = this.registerCustomListener<T>(event, (data) => subscriber.next(data));
  //     return () => off(); // precise teardown
  //   }).pipe(share()); // share across multiple subscribers if needed
  // }

  public on$<T = unknown>(event: string): Observable<T> {
    if (!this.streams.has(event)) {
      const stream = new Observable<T>(subscriber => {
        const off = this.registerCustomListener<T>(event, data => subscriber.next(data));
        return () => off();
      }).pipe(
        // keep one socket listener, share among subscribers
        share({
          connector: () => new Subject<T>(),
          resetOnComplete: false,
          resetOnError: false,
          resetOnRefCountZero: true
        })
      );
      this.streams.set(event, stream);
    }
    return this.streams.get(event)! as Observable<T>;
  }

  public onSticky$<T>(event: string): Observable<T> {
    if (!this.streams.has(event)) {
      const stream = new Observable<T>(subscriber => {
        const off = this.registerCustomListener<T>(event, data => subscriber.next(data));
        return () => off();
      }).pipe(
        share({
          connector: () => new Subject<T>(),
          resetOnComplete: false,
          resetOnError: false,
          resetOnRefCountZero: false  // <- key
        })
      );
      this.streams.set(event, stream);
    }
    return this.streams.get(event)!;
  }

  // Then define your typed stream like this (ensure the EVENT NAME is correct):
  public readonly sessionUpdates$ = this.on$<{ session: GameSession | null; message: string }>('session_updated');


  /** Connect with current access token. Idempotent-ish: replaces old socket cleanly. */
  public connect(): void {
    this.debugLog('Attempting to connect...');

    const token = this.tokenService.getAccessToken();
    if (!token) {
      this.debugLog('No token found, skipping connection');
      return;
    }

    // ★ build socket once; reuse across reconnects (preserves listeners)
    if (!this.socket) {
      this.socket = io(environment.wsUrl, {
        autoConnect: false,                    // ★ connect manually
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
        transports: ['websocket', 'polling'],  // ★ explicit transports
        // connectionStateRecovery: true,         // ★ helps after brief offline
        // ★ CRITICAL: auth as a function (fresh token each (re)connect)
        auth: (cb) => cb({ token: this.tokenService.getAccessToken() }),
      });

      this.setupSocketListeners();
      this.attachCustomListeners();
    }

    // Avoid duplicate connect calls
    if (!this.socket.active) {
      this.socket.connect();
    }
  }

  /** Optional helper to update auth token without full rebuild (socket.io v4 supports it). */
  public updateAuthToken(token: string) {
    if (this.socket) {
      // ★ supply a function, not a static object
      (this.socket.io as any).opts.auth = (cb: any) => cb({ token });
      this.debugLog('Socket auth token updated');
    }
  }

  public disconnect(): void {
    if (!this.socket) {
      this.debugLog('No socket connection to disconnect');
      return;
    }
    this.debugLog('Disconnecting socket');
    this.socket.disconnect();
  }

  public emit(event: string, data?: any): void {
    if (!this.socket) {
      this.debugLog('Cannot emit, no socket connection:', event);
      return;
    }
    this.debugLog('Emitting:', event, data);
    this.socket.emit(event, data);
  }

  /** Simple imperative on (rarely needed if you use registerCustomListener/on$). */
  public on<T>(event: string, callback: (data: T) => void): void {
    if (!this.socket) {
      this.debugLog('Cannot listen, no socket connection:', event);
      return;
    }
    this.socket.on(event, callback);
  }
  public off<T>(event: string, callback: (data: T) => void): void {
    this.socket?.off(event, callback);
  }

  // USER-WIDE events (modal on any page listens to these)
  public readonly aiJobStarted$ = this.onSticky$<AIJobSocketResponse>('aiq:job.started');
  public readonly aiJobProgress$ = this.onSticky$<AIJobSocketResponse>('aiq:job.progress');
  public readonly aiJobCompleted$ = this.onSticky$<AIJobSocketResponse>('aiq:job.completed');
  public readonly aiJobFailed$ = this.onSticky$<AIJobSocketResponse>('aiq:job.failed');

  // PER-JOB events (if a screen wants fine-grained updates for one job)
  public joinAiJob(jobId: number) {
    const room = `aiq:job:${jobId}`;
    this.join(room);
    const progress$ = this.onSticky$<AIJobSocketResponse>('aiq:progress')
      .pipe(filter(p => p.jobId === jobId));
    const completed$ = this.onSticky$<AIJobSocketResponse>('aiq:completed')
      .pipe(filter(p => p.jobId === jobId));
    const error$ = this.onSticky$<AIJobSocketResponse>('aiq:error')
      .pipe(filter(p => p.jobId === jobId));
    const leave = () => this.leave(room);
    return { progress$, completed$, error$, leave };
  }

  public join(room: string): void {
    if (!room) return;

    this.rooms.add(room);

    if (!this.socket) { this.debugLog('Cannot join, no socket'); return; }

    if (this.socket.connected) {
      this.socket.emit('join', room);
      this.debugLog('join. Joined room:', room);
    } else {
      this.debugLog('join. Queued room until connect:', room);
    }
  }

  public leave(room: string): void {
    if (!room) return;

    this.rooms.delete(room);

    if (!this.socket) return;

    this.socket.emit('leave', room);
    this.debugLog('Left room:', room);
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.debugLog('Socket connected');
      this.connectionStatus$.next('connected');

      for (const room of this.rooms) {
        this.socket!.emit('join', room);
        this.debugLog('setupSocketListeners. Joined room:', room);
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.debugLog('Socket disconnected:', reason);
      this.connectionStatus$.next('disconnected');
      this.stopPingMeasurement();
      this.latency$.next(-1);
    });

    // ★ robust connect_error handling (expired token, offline, etc.)
    this.socket.on('connect_error', async (error: any) => {
      const code = error?.data?.code || error?.message || '';
      this.debugLog('Connection error:', { code, raw: error });

      // Network offline → let socket.io retry; nothing to do
      if (code === 'ERR_INTERNET_DISCONNECTED' || navigator.onLine === false) {
        this.debugLog('Detected offline; waiting for network to return');
        // this.snackBar.open(
        //   'No internet connection',
        //   'Close',
        //   { duration: 2000, panelClass: 'errorNotificationMessage' }
        // );
        return;
      }

      // TOKEN_EXPIRED → pause retries, refresh, set new auth, hard-reconnect
      if (code === 'TOKEN_EXPIRED') {
        this.debugLog('Token expired');
        if (this.refreshing) return;
        this.refreshing = true;

        const ioManager: any = this.socket!.io;
        const prevReconn = ioManager.opts.reconnection;
        try {
          this.debugLog('Token expired, refreshing...');
          // 1) stop the retry loop while we refresh
          ioManager.opts.reconnection = false;

          // 2) perform silent refresh (implement inside TokenService)
          await firstValueFrom(this.authService.refreshToken());

          // 3) inject fresh auth provider
          ioManager.opts.auth = (cb: any) =>
            cb({ token: this.tokenService.getAccessToken() });

          // 4) force a brand-new engine (drop stale sid)
          ioManager.opts.forceNew = true;

          // 5) connect now
          this.socket!.connect();
        } catch (e) {
          this.debugLog('Refresh failed, logging out', e);
          this.store.dispatch(AuthActions.logout());
        } finally {
          ioManager.opts.reconnection = prevReconn;
          this.refreshing = false;
        }
        return;
      }

      // Optional: handle your legacy message
      if (code === 'Authentication failed') {
        this.store.dispatch(AuthActions.logout());
      }
    });
  }

  /** Re-attach any previously registered custom listeners to the NEW socket instance. */
  private attachCustomListeners(): void {
    if (!this.socket) return;
    Object.entries(this.customListeners).forEach(([event, callbacks]) => {
      callbacks.forEach(cb => this.socket!.on(event, cb));
    });
  }

  // ---- ping / latency ----

  public startPingMeasurement(): void {
    if (!this.socket || this.pingInterval) return;
    this.debugLog('startPingMeasurement');

    // initial measure
    this.measurePing();

    this.pingInterval = setInterval(() => {
      if (this.socket?.connected) this.measurePing();
      else this.stopPingMeasurement();
    }, 5000);

    // also stop on disconnect (avoid multiple handlers; remove old one first)
    this.socket.off('disconnect', this.handleDisconnectStopPing);
    this.socket.on('disconnect', this.handleDisconnectStopPing);
  }

  private handleDisconnectStopPing = () => {
    this.stopPingMeasurement();
    this.latency$.next(-1);
  };

  public stopPingMeasurement(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private measurePing(): void {
    const start = Date.now();
    this.socket?.emit('ping_probe');
    this.socket?.once('pong_probe', () => {
      const latency = Date.now() - start;
      this.latency$.next(latency);
    });
  }

  public trackJobQueue(jobId: number | null): void {
    // console.log('trackJobQueue', jobId);
    if (!jobId) return;
    this.socket?.emit('aiq:queue.subscribe', { jobId });
  }

  public untrackJobQueue(jobId: number | null): void {
    // console.log('untrackJobQueue', jobId);
    if (!jobId) return;
    this.socket?.emit('aiq:queue.unsubscribe', { jobId });
  }

  getLatency(): Observable<number> {
    return this.latency$.asObservable();
  }

  getConnectionStatus(): Observable<ConnStatus> {
    return this.connectionStatus$.asObservable();
  }

  private debugLog(...args: any[]): void {
    if (this.DEBUG) console.log('[Socket]', this.instanceId, ...args);
  }

  ngOnDestroy(): void {
    this.stopPingMeasurement();
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionStatus$.complete();
    this.latency$.complete();
  }
}
